import { MDF, NewSparkle, Pricetag } from "@/components/bnp-icons";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";
import React, { useState, useEffect } from "react";

export type CountdownTime = {
  days: number;
  hours: number;
  minutes: number;
};

type PropertyStats = {
  amountRaised: number;
  subscriberCount: number;
  yearlyReturn: number;
  fundedDate: string;
  minimumBuyIn: number;
  developmentType: string;
  offerEndsDate: string;
  image_url: string[];
};

interface InvestmentCardProps {
  title: string;
  amountRequired: number;
  propertyStats: PropertyStats;
  initialCountdown?: CountdownTime;
  totalSlides?: number;
  currentSlide?: number;
  documentType?: string;
  className?: string;
  currencyPrefix?: string;
  imageUrl?: string;
}

const formatCurrency = (amount: number, prefix: string = "NGN"): string => {
  return `${prefix} ${amount?.toLocaleString()}`;
};

export const getDevelopmentType = (type: string) => {
  switch (type) {
    case "joint-venture":
      return "JVP";

    case "off-plan-sale":
      return "OPS";

    case "sponsorship-contract":
      return "SO";

    case "fractional-ownership":
      return "FO";

    default:
      break;
  }
};

export const getDevelopmentTypeName = (type: string) => {
  switch (type) {
    case "joint-venture":
      return "JOINT VENTURE";

    case "off-plan-sale":
      return "OFF-PLAN SALE";

    case "sponsorship-contract":
      return "SPONSORSHIP CONTRACT";

    case "fractional-ownership":
      return "FRACTIONAL OWNERSHIP";

    default:
      break;
  }
};

const InvestmentCard: React.FC<any> = ({
  title,
  imageUrl = "/assets/images/property.png",
  amountRequired,
  propertyStats,
  className = "",
  currencyPrefix = "NGN",
}) => {
  const [timeLeft, setTimeLeft] = useState<CountdownTime>({
    days: 0,
    hours: 0,
    minutes: 0,
  });

  const progressPercentage =
    (propertyStats.amountRaised / amountRequired) * 100;

  useEffect(() => {
    const endDate = new Date(
      propertyStats?.preferredListingEndDate ||
        propertyStats?.deliveryDate ||
        propertyStats?.targetRedemptionDate,
    );

    console.log("Property stats", propertyStats);

    console.log(endDate);

    if (endDate && !isNaN(endDate.getTime())) {
      const calculateTimeLeft = () => {
        const now = new Date();
        const difference = endDate.getTime() - now.getTime();

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor(
            (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
          );
          const minutes = Math.floor(
            (difference % (1000 * 60 * 60)) / (1000 * 60),
          );

          return { days, hours, minutes };
        }

        return { days: 0, hours: 0, minutes: 0 };
      };

      setTimeLeft(calculateTimeLeft());

      const timer = setInterval(() => {
        setTimeLeft(calculateTimeLeft());

        const current = calculateTimeLeft();
        if (
          current.days === 0 &&
          current.hours === 0 &&
          current.minutes === 0
        ) {
          clearInterval(timer);
        }
      }, 60000);

      return () => clearInterval(timer);
    }
  }, [
    propertyStats?.preferredListingEndDate,
    propertyStats?.deliveryDate,
    propertyStats?.targetRedemptionDate,
  ]);

  return (
    <div
      className={`lg:min-w-[350px] lg:max-w-[350px] space-y-6 bg-white rounded-xl shadow-lg ${className}`}
    >
      <div className="font-sans text-base lg:max-w-[350px] h-[688px] bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="absolute z-10 flex justify-between p-4 w-[90%] lg:w-[350px]">
          {/* <div className="flex justify-center font-bold items-center gap-2 bg-base-white text-bnp-dark p-2 lg:p-0  text-sm lg:w-[100px] h-[28px] rounded-lg">
            <svg
              width="8"
              height="8"
              viewBox="0 0 4 4"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="2.07611" cy="1.78705" r="1.66205" fill="#2D96A7" />
            </svg>

            <span className="font-bold">Active</span>
          </div> */}
          {/* <div className="flex justify-center items-center gap-2 bg-bnp-dark p-2 lg:p-0 text-sm text-white rounded-lg lg:w-[123px] h-[28px]">
            <NewSparkle />
            <span className="font-bold">New listing</span>
          </div> */}
        </div>

        <div className="relative w-full h-[200px]">
          <img
            src={
              propertyStats?.image_url?.length > 0
                ? propertyStats?.image_url[0]
                : "/assets/images/property-default.svg"
            }
            alt={title}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative -mt-5 w-[95%]  ml-auto mr-auto p-4 bg-white rounded-t-xl">
          <div className="bg-white rounded-t-xl">
            <div className="space-y-1 mb-4">
              <h2 className="max-w-[100%] text-base font-bold text-dark-blue truncate">
                {capitalizeFirstLetter(title)}
              </h2>
            </div>

            <div className="flex justify-between items-center mb-3">
              <div>
                <p className="text-grey-4 text-sm mb-2">Amount required</p>
                <p className="text-lg font-semibold text-bnp-cyan">
                  {formatCurrency(Math.floor(amountRequired), currencyPrefix)}
                </p>
              </div>
              <div className="flex items-center gap-2 text-dark-blue">
                <MDF />
                <span>
                  {getDevelopmentType(propertyStats?.developmentType)}
                </span>
              </div>
            </div>

            <div className="bg-grey-6 p-3 rounded-lg">
              <div className="h-2 rounded-full bg-dark-blue mb-3">
                <div
                  className="h-full bg-bnp-cyan rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, progressPercentage)}%` }}
                />
              </div>

              <div className="space-y-4 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-grey-4 font-medium">
                    {propertyStats.developmentType === "off-plan-sale"
                      ? "Subscription Total"
                      : "Amount raised"}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(
                      Math.floor(propertyStats.amountRaised),
                      currencyPrefix,
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-grey-4 font-medium">Subscribers</span>
                  <span className="font-semibold text-gray-900">
                    {propertyStats.subscriberCount}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-grey-4 font-medium">
                    {propertyStats.developmentType === "off-plan-sale"
                      ? "Initial payment"
                      : "Expected returns"}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {propertyStats.developmentType === "off-plan-sale" ? (
                      formatCurrency(
                        Math.floor(propertyStats.initialDeposit),
                        currencyPrefix,
                      )
                    ) : (
                      <>{Math.floor(propertyStats.expectedReturns)}%</>
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-grey-4 font-medium">
                    {propertyStats.developmentType === "off-plan-sale"
                      ? "Monthly payment"
                      : "Funded Date"}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {propertyStats.developmentType === "off-plan-sale"
                      ? formatCurrency(
                          Math.floor(propertyStats.subsequentMonthlyDeposit),
                          currencyPrefix,
                        )
                      : (propertyStats.fundedDate ?? "-")}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-grey-4 font-medium">
                    {propertyStats.developmentType === "off-plan-sale"
                      ? "Amount per unit"
                      : "Minimum buy-in"}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {propertyStats.developmentType === "off-plan-sale"
                      ? formatCurrency(
                          Math.floor(propertyStats.amountPerUnit),
                          currencyPrefix,
                        )
                      : formatCurrency(
                          Math.floor(propertyStats.minimumBuyIn),
                          currencyPrefix,
                        )}
                  </span>
                </div>
              </div>

              <div className="bg-bnp-teal rounded-sm p-4 mt-5">
                <p className="text-center text-grey-4 mb-4">Offer ends</p>
                <div className="flex justify-center text-sm gap-4 md:gap-8">
                  {timeLeft &&
                    [
                      { value: timeLeft.days, label: "Days" },
                      { value: timeLeft.hours, label: "Hours" },
                      { value: timeLeft.minutes, label: "Mins" },
                    ].map(({ value, label }) => (
                      <div key={label} className="text-center">
                        <div className="text-sm font-bold text-dark-blue">
                          {String(value || 0).padStart(2, "0")}
                        </div>
                        <div className="text-sm text-grey-4">{label}</div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentCard;
